import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './report-detail.module.css';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ErrorComponent from '../../components/error/error';
import FeatureTitle from '../../components/feature-title/feature-title';
import Loading from '../../components/loading/loading';
import SubmitButton from '../../components/submit-button/submit-button';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import ReportService from '../../services/report-service';
import SchoolService from '../../services/school-service';
import StorageService from '../../services/storage-service';

const ReportDetail = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const { setToastContent } = useContext(GeneralContext);

  const [error, setError] = useState(false);
  const [report, setReport] = useState<ReportDetail>(null);
  const [schoolUuid, setSchoolUuid] = useState<string>(null);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [isGenerating, setIsGenerating] = useState(false);

  const pollingRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    const storageService = new StorageService();
    const schoolService = new SchoolService();
    const schoolId = storageService.getEmployee().schoolId;

    schoolService
      .getSchoolById(schoolId)
      .then((school) => {
        setSchoolUuid(school.uuid);
        const reportService = new ReportService(school.uuid);
        return reportService.getReport(reportId);
      })
      .then((reportDetail) => {
        const defaults: Record<string, any> = {};
        reportDetail.filters.forEach((f) => {
          if (f.defaultValue != null) {
            defaults[f.name] = f.defaultValue;
          }
        });
        if (Object.keys(defaults).length > 0) {
          setFilterValues((prev) => ({ ...defaults, ...prev }));
        }
        setReport(reportDetail);
      })
      .catch(() => setError(true));

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (name: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (name: string, option: string, checked: boolean) => {
    setFilterValues((prev) => {
      const current: string[] = prev[name] || [];
      const updated = checked
        ? [...current, option]
        : current.filter((v: string) => v !== option);
      return { ...prev, [name]: updated };
    });
  };

  const allRequiredFilled = useCallback(() => {
    if (!report) return false;
    return report.filters
      .filter((f) => f.required)
      .every((f) => {
        const val = filterValues[f.name];
        if (Array.isArray(val)) return val.length > 0;
        return val !== undefined && val !== '';
      });
  }, [report, filterValues]);

  const handleDownload = () => {
    if (!schoolUuid || isGenerating) return;

    setIsGenerating(true);
    const reportService = new ReportService(schoolUuid);

    reportService
      .generateReport(reportId, filterValues)
      .then((response) => {
        pollingRef.current = setInterval(() => {
          reportService
            .getResult(reportId, response.resultId)
            .then((result) => {
              if (result.status === 'completed') {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
                setIsGenerating(false);
                window.open(result.downloadUrl, '_blank');
                setToastContent({
                  isOpen: true,
                  text: 'Relatório gerado com sucesso!',
                  type: 'success',
                });
              } else if (result.status === 'failed') {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
                setIsGenerating(false);
                setToastContent({
                  isOpen: true,
                  text: 'Erro ao gerar relatório. Tente novamente.',
                  type: 'error',
                });
              }
            })
            .catch(() => {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              setIsGenerating(false);
              setToastContent({
                isOpen: true,
                text: 'Erro ao verificar status do relatório.',
                type: 'error',
              });
            });
        }, 2500);
      })
      .catch(() => {
        setIsGenerating(false);
        setToastContent({
          isOpen: true,
          text: 'Erro ao solicitar relatório.',
          type: 'error',
        });
      });
  };

  const renderFilter = (filter: ReportFilter) => {
    switch (filter.type) {
      case 'Date':
        return (
          <input
            type='date'
            className={styles.input}
            value={filterValues[filter.name] || ''}
            onChange={(e) => handleFilterChange(filter.name, e.target.value)}
          />
        );
      case 'Text':
        return (
          <input
            type='text'
            className={styles.input}
            value={filterValues[filter.name] || ''}
            onChange={(e) => handleFilterChange(filter.name, e.target.value)}
          />
        );
      case 'Year':
        return (
          <input
            type='number'
            className={styles.input}
            min='2000'
            max='2100'
            value={filterValues[filter.name] || ''}
            onChange={(e) => handleFilterChange(filter.name, e.target.value)}
          />
        );
      case 'Select':
        return (
          <select
            className={styles.select}
            value={filterValues[filter.name] || ''}
            onChange={(e) => handleFilterChange(filter.name, e.target.value)}
          >
            <option value=''>Selecione...</option>
            {(filter.options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      case 'MultiSelect':
        return (
          <div className={styles.checkboxGroup}>
            {filter.options?.map((opt) => (
              <label key={opt.value} className={styles.checkboxLabel}>
                <input
                  type='checkbox'
                  checked={(filterValues[filter.name] || []).includes(opt.value)}
                  onChange={(e) => handleMultiSelectChange(filter.name, opt.value, e.target.checked)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  if (error) return <ErrorComponent type='contentFull' />;

  if (!report) return <Loading type='contentFull' />;

  return (
    <BaseContentContainer
      format={BaseContentContainerFormat.singleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.top}
    >
      <FeatureTitle title={report.name} />

      <div className={styles.container}>
        <p className={styles.description}>{report.description}</p>

        <div className={styles.filtersContainer}>
          {report.filters.map((filter) => (
            <div key={filter.name} className={styles.filterField}>
              <label className={styles.label}>
                {filter.label}
                {filter.required && <span className={styles.required}> *</span>}
              </label>
              {renderFilter(filter)}
            </div>
          ))}
        </div>

        <div className={styles.actions}>
          <SubmitButton
            value={isGenerating ? 'Gerando relatório...' : 'Gerar relatório'}
            variant='primary'
            disabled={!allRequiredFilled() || isGenerating}
            onClick={handleDownload}
          />
        </div>
      </div>
    </BaseContentContainer>
  );
};

export default ReportDetail;
